import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import type { UserSubscriptions, ErrorResponse, ReasonCodesOption } from 'tsconfig/types.d';
import { initCommerceApiWithGlobals } from 'common-api';
import { isBrowser } from '../helpers/utils';
import { logger } from 'common-ui';

interface SubscriptionState {
  subscriptionData: UserSubscriptions | undefined;
  error: any;
  cancelReasons: ReasonCodesOption[];
}

interface SubscriptionActions {
  setSubscriptionData: (data: UserSubscriptions) => void;
  setError: (error: any) => void;
  setCancelReasons: (reasons: ReasonCodesOption[]) => void;
  reset: () => void;
}

export type SubscriptionStore = SubscriptionState & SubscriptionActions;

const initialState: SubscriptionState = {
  subscriptionData: undefined,
  error: undefined,
  cancelReasons: [],
};

export const useSubscriptionStore = create<SubscriptionStore>()(
  devtools(
    set => ({
      ...initialState,
      setSubscriptionData: data => set({ subscriptionData: data }, undefined, 'setSubscriptionData'),
      setError: error => set({ error }, undefined, 'setError'),
      setCancelReasons: reasons => set({ cancelReasons: reasons }, undefined, 'setCancelReasons'),
      reset: () => set(initialState, undefined, 'reset'),
    }),
    { name: 'SubscriptionStore' },
  ),
);

// Derived selector
export const selectSubscriptionDetails = (state: SubscriptionState) => {
  if (state.subscriptionData !== undefined && state.subscriptionData) {
    return {
      subscriptionData: state.subscriptionData,
      subscriptionStatus: 'success' as const,
    };
  }
  return {
    subscriptionStatus: 'error' as const,
    message: state.error,
  };
};

export const useInitSubState = () => async () => {
  if (!isBrowser()) {
    logger?.log('useInitSubState skipping fetch as not in browser');
    return;
  }
  if (useSubscriptionStore.getState().subscriptionData) {
    logger?.log('skipping the fetch call if already contains');
    return;
  }
  try {
    const commerceApi = initCommerceApiWithGlobals();
    const newData: UserSubscriptions = await commerceApi.Subscription.get();
    useSubscriptionStore.getState().setSubscriptionData(newData);
  } catch (err) {
    const apiError = err as ErrorResponse;
    useSubscriptionStore.getState().setError(apiError?.response?.data?.message || apiError?.message);
    logger?.error('API ERROR', err);
  }
};
